import { useCallback } from 'react'
import { TreeSelectWithSummary } from '../components/TreeSelectWithSummary'
import { CollapsibleOpenState } from '../components/CollapsibleSelectField'
import { useProjectPlanSelectOptions } from './hooks/useProjectPlanSelectOptions'
import { intl } from '../../../../../../i18n'
import { VersionReportVersionPageRowEntity } from '../../../../../../domain/entity/version-report/VersionReportVersionEntity'

type Props = {
  snapshotUuid: string
  value: string[]
  onChange: (value: string[]) => void
  multiSelect?: boolean
  height?: string
  // 概況ページ専用: 子を選んだら表示される祖先も選択に含める
  includeAncestors?: boolean
  rows?: VersionReportVersionPageRowEntity[]
  // チーム別ページ専用: 選択済みをチップ表示にし、選択操作自体を折りたたむ
  collapsible?: boolean
  // チケット管理表との切替で開閉状態を保つため、呼び出し側で状態を持つ場合に渡す
  openState?: CollapsibleOpenState
}

export const ProcessTreeSelect = ({
  snapshotUuid,
  value,
  onChange,
  multiSelect = true,
  height,
  includeAncestors = false,
  rows,
  collapsible = false,
  openState,
}: Props) => {
  const { options, parentMap, isLoading } = useProjectPlanSelectOptions({
    snapshotUuid,
  })

  const handleChange = useCallback(
    (next: string[]) => {
      if (!includeAncestors) {
        onChange(next)
        return
      }
      const nextSet = new Set(next)
      const removed = value.find(uuid => !nextSet.has(uuid))
      if (removed) {
        onChange(next.filter(uuid => !isDescendantOf(uuid, removed, parentMap)))
      } else {
        onChange(withAncestors(next, parentMap))
      }
    },
    [onChange, includeAncestors, parentMap, value]
  )

  return (
    <TreeSelectWithSummary
      options={options}
      parentMap={parentMap}
      value={value}
      onChange={handleChange}
      multiSelect={multiSelect}
      height={height}
      loading={isLoading}
      rows={rows}
      collapsible={collapsible}
      openState={openState}
      summaryLabel={intl.formatMessage({
        id: 'versionReport.treeSelect.process.summaryLabel',
      })}
    />
  )
}

const withAncestors = (
  uuids: string[],
  parentMap: Record<string, string>
): string[] => {
  const result = new Set<string>()
  for (const uuid of uuids) {
    let current: string | undefined = uuid
    while (current && !result.has(current)) {
      result.add(current)
      current = parentMap[current]
    }
  }
  return Array.from(result)
}

const isDescendantOf = (
  node: string,
  ancestor: string,
  parentMap: Record<string, string>
): boolean => {
  let current: string | undefined = parentMap[node]
  while (current) {
    if (current === ancestor) return true
    current = parentMap[current]
  }
  return false
}
